import { Button } from '@/components/ui/Button';
import { Wordmark } from '@/components/ui/Wordmark';
import { useEffect } from 'react';
import { reportClient } from '@/lib/log/clientLog';
import { useProfileStore } from '@/store/useProfileStore';
import { restoreSession, signOut } from '@/services/authService';
import { CodeSignIn } from './CodeSignIn';

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 px-4 text-center">
      <Wordmark />
      {children}
    </div>
  );
}

export function Splash() {
  return (
    <Frame>
      <p className="text-sm text-ink-faint">Loading…</p>
    </Frame>
  );
}

export function NoAccessPage() {
  return (
    <Frame>
      <p className="max-w-sm text-ink-soft">
        You're signed in, but this account hasn't been given access to Simon yet. Ask the team to set up your role.
      </p>
      <Button variant="secondary" onClick={() => void signOut()}>
        Sign out
      </Button>
    </Frame>
  );
}

export function SessionErrorPage() {
  return (
    <Frame>
      <p className="max-w-sm text-ink-soft">Simon couldn't load your account. Check your connection and try again.</p>
      <Button onClick={() => void restoreSession()}>Try again</Button>
    </Frame>
  );
}

export function NotFoundPage() {
  return (
    <Frame>
      <p className="text-ink-soft">That page doesn't exist.</p>
      <a href="/" className="text-sm font-semibold text-sage underline">
        Back to Simon
      </a>
    </Frame>
  );
}

export function ForbiddenPage() {
  return (
    <Frame>
      <p className="max-w-sm text-ink-soft">Your role doesn't include this page.</p>
      <a href="/" className="text-sm font-semibold text-sage underline">
        Back to Simon
      </a>
    </Frame>
  );
}

/**
 * Blocking step for an MFA-pending session (sreg-api device MFA): this device
 * hasn't been verified in the last two weeks. Nothing else is reachable — the
 * API and the BFF refuse the session until a code is entered.
 */
export function VerifyDevicePage() {
  const email = useProfileStore((s) => s.me?.profile.email) ?? '';
  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-2xl bg-surface p-8 shadow-sm">
        <Wordmark />
        <h1 className="mb-2 mt-6 text-lg font-semibold text-ink">Verify this device</h1>
        <p className="mb-6 text-sm text-ink-soft">
          For your security, Simon asks for an emailed code on a new device, and every two weeks after that.
        </p>
        <CodeSignIn email={email} onSignedIn={() => undefined} />
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-6 w-full text-xs font-semibold text-ink-faint hover:text-ink"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

/** A render crash anywhere under the router: reported, with a way back. */
export function CrashPage({ error }: { error: unknown }) {
  useEffect(() => {
    reportClient({
      source: 'render',
      level: 'ERROR',
      message: error instanceof Error ? error.message : String(error),
      error,
    });
  }, [error]);
  return (
    <Frame>
      <p className="max-w-sm text-ink-soft">Something went wrong on this page. The team has been notified.</p>
      <Button onClick={() => window.location.reload()}>Reload</Button>
    </Frame>
  );
}
